import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import request from "../../../api/request";
import "./UploadPreviewTable.css";
import "./UploadOrder.css";
import { BackendAPI } from "../../../const/api";
import { formatOrderLineError } from "./UploadOrder";
import EditModal from "../../../component/EditModal/EditModal";
import { useToast } from "../../../component/ToastBar";

interface TrackingPreviewData {
  orderId: string;
  material: string;
  quantity: number;
  trackingNumber: string;
  carrier: string;
  billingName: string;
  billingTel: string;
  billingAddress: string;
  shippingAddress: string;
  errors?: string[];
  index?: number;
}

const defaultTrackingPreview = {
  orderId: "0",
  material: "model",
  quantity: 0,
  trackingNumber: "",
  carrier: "",
  billingName: "",
  billingTel: "",
  billingAddress: "",
  shippingAddress: "",
  errors: [],
};

const UploadTracking = () => {
  const toast = useToast();
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<TrackingPreviewData[]>([]);
  const [editData, setEditData] = useState<TrackingPreviewData>({
    ...defaultTrackingPreview,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigator = useNavigate();
  const allowedExtensions = [".csv", ".xlsx", ".xls"]; // 允许的文件扩展名

  const sendFileToServer = async (formData) => {
    try {
      return await request.post(
        BackendAPI.trackingUploadPreview,
        null,
        formData,
      );
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.slice(
        ((selectedFile.name.lastIndexOf(".") - 1) >>> 0) + 2,
      );
      if (allowedExtensions.includes(`.${fileExtension.toLowerCase()}`)) {
        setFile(selectedFile);
        setSelectedFileName(selectedFile.name); // 设置已选择的文件名
      } else {
        toast("请选择有效的文件", "error"); // 提示用户选择有效文件
        e.target.value = ""; // 重置文件输入框
        setFile(null);
        setSelectedFileName("");
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await sendFileToServer(formData);
      if (response.success) {
        toast("上传成功");
        setPreviewData(response.data); // 假设后端返回的数据中有一个 data 字段表示实际数据
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast("上传文件失败，请检查文件格式或者上报故障", "error");
    }
  };

  const renderPreviewTable = () => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>订单编号</TableCell>
            <TableCell>物料</TableCell>
            <TableCell>数量</TableCell>
            <TableCell>承运商</TableCell>
            <TableCell>物流单号</TableCell>
            <TableCell>发货地址</TableCell>
            <TableCell>收件人</TableCell>
            <TableCell>手机号</TableCell>
            <TableCell>收件地址</TableCell>
            <TableCell>错误信息</TableCell>
            <TableCell>操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {previewData.map((row: TrackingPreviewData, index) => (
            <TableRow
              key={index}
              style={{
                backgroundColor:
                  (row.errors?.length || 0) > 0 ? "#ffcccc" : "transparent",
              }}
            >
              {/* 表格行数据 */}
              <TableCell>{row.orderId}</TableCell>
              <TableCell>{row.material}</TableCell>
              <TableCell>{row.quantity}</TableCell>
              <TableCell>{row.carrier}</TableCell>
              <TableCell>{row.trackingNumber}</TableCell>
              <TableCell>{row.shippingAddress}</TableCell>
              <TableCell>{row.billingName}</TableCell>
              <TableCell>{row.billingTel}</TableCell>
              <TableCell>{row.billingAddress}</TableCell>
              <TableCell>
                {row.errors?.map((e) => formatOrderLineError(e)).join(";\n") ||
                  "-"}
              </TableCell>
              <TableCell>
                <button onClick={() => handleEdit(index)}>修改</button>
                <button onClick={() => handleDelete(index)}>删除</button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const handleEdit = (index) => {
    setEditData({ ...previewData[index], index });
    setIsEditModalOpen(true);
  };

  const handleDelete = (index) => {
    setPreviewData(previewData.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      const response = await request.post(BackendAPI.trackingUploadConfirm, {
        tracking: previewData,
      });
      if (!response.success) {
        // 仍然有错误，接受previewData，重新渲染表单，并提示用户
        toast("提交失败, 数据存在错误", "error");
        const data = response.data.tracking;
        setPreviewData(data);
      } else {
        toast("提交成功");
        navigator("/dashboard/orders");
      }
    } catch (error) {
      console.error("Error submitting orders:", error);
      toast("数据上传出错，请上报故障", "error");
    }
  };

  const hasErrors = previewData.some((row) => (row.errors?.length || 0) > 0);

  const handleSaveEdit = () => {
    const updatedPreviewData = [...previewData];
    if (editData.index === undefined) {
      return;
    }
    const index = editData.index;
    const previousData = updatedPreviewData[index];
    const newErrors: string[] = [];
    const newData = { ...editData };
    if (
      (previousData.errors?.filter((e) => e === "ORDER_NOT_SHIPPABLE").length ||
        0) > 0
    ) {
      newErrors.push("ORDER_NOT_SHIPPABLE");
    }
    if (!newData.carrier) {
      newErrors.push("CARRIER_NOT_FOUND");
    }
    if (!newData.trackingNumber) {
      newErrors.push("TRACKING_NUMBER_NOT_FOUND");
    }
    if (
      !newData.billingName ||
      !newData.billingTel ||
      !newData.billingAddress
    ) {
      newErrors.push("BILLING_INFO_NOT_FOUND");
    }
    newData.errors = newErrors;
    updatedPreviewData[index] = newData;
    setPreviewData(updatedPreviewData);
    setIsEditModalOpen(false);
  };

  const fields = () => {
    return [
      {
        label: "承运商",
        type: "text" as const,
        value: editData.carrier,
        onChange: (value) => setEditData({ ...editData, carrier: value }),
      },
      {
        label: "物流单号",
        type: "text" as const,
        value: editData.trackingNumber,
        onChange: (value) =>
          setEditData({ ...editData, trackingNumber: value }),
      },
      {
        label: "发货地址",
        type: "text" as const,
        value: editData.shippingAddress,
        onChange: (value) =>
          setEditData({ ...editData, shippingAddress: value }),
      },
    ];
  };

  return (
    <div>
      <div className="upload-container">
        <label htmlFor="file-input" className="upload-button">
          选择文件
        </label>
        <input
          type="file"
          id="file-input"
          onChange={handleFileChange}
          accept={allowedExtensions.join(",")}
        />
        <span className="file-info">
          （支持 {allowedExtensions.join(",")} 格式）
        </span>
        {selectedFileName && (
          <span className="selected-file-info">{selectedFileName}</span>
        )}
        <button onClick={handleUpload} className="submit-button">
          上传预览
        </button>
      </div>
      <div className="info-text">
        <p>
          请使用以下模板文件进行数据上传：
          <a
            href="/templates/物流上传模板.xlsx"
            download
            className="download-template-link"
          >
            下载模板
          </a>
        </p>
        <p>
          <span>
            表格请不要带格式、函数、宏、公式、合并单元格，如标题行的过滤器、公式单元格等
          </span>
        </p>
        <p>
          <span>仓库管理员上传带物流号的订单列表，订单状态将更改为已发货</span>
        </p>
      </div>
      {previewData.length > 0 && renderPreviewTable()}
      {previewData.length > 0 && !isEditModalOpen && (
        <button onClick={handleSubmit} disabled={hasErrors}>
          确认提交
        </button>
      )}
      {isEditModalOpen && (
        <EditModal
          fields={fields()}
          onSave={handleSaveEdit}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}
    </div>
  );
};

export default UploadTracking;
